import { useTranslate } from '@tolgee/react';
import { useToggle } from 'ahooks';
import { Skeleton } from 'antd';
import { useMemo } from 'react';
import { ToggleButton } from './LightConversation/components/ToggleButton';

export interface HistoryItemProps {
  title?: string | React.ReactNode;
  description?: string | React.ReactNode;
  last?: boolean;
}

const Item = ({ title, description, last }: HistoryItemProps) => {
  return (
    <div className="flex items-stretch">
      {/* 左侧：圆圈 + 虚线引导线 */}
      <div className="flex flex-col items-center w-4 flex-shrink-0">
        <div className="w-2 h-2 rounded-full border border-solid border-[#707070] flex-shrink-0 mt-[6px]" />
        {last ? null : (
          <div className="flex-1 border-0 border-l border-dashed border-[#707070] mt-1" />
        )}
      </div>
      <div className="flex-1 pl-2 pb-4">
        <div className="text-[var(--fett-text-primary)]">{title}</div>
        {description}
      </div>
    </div>
  );
};

/** 骨架屏：与 History Item 布局一致，用于 loading 状态 */
const HistorySkeleton = () => {
  return (
    <div className="flex flex-col">
      {[1, 2, 3].map((i) => (
        <div key={i} className="flex items-stretch">
          <div className="flex flex-col items-center w-4 flex-shrink-0">
            <div className="w-2 h-2 rounded-full bg-[#e5e5e5] flex-shrink-0 mt-[6px]" />
            <div className="flex-1 border-0 border-l border-dashed border-[#e5e5e5] mt-1" />
          </div>
          <div className="flex-1 pl-2 pb-4 flex flex-col gap-2">
            <Skeleton.Input active size="small" className="!w-full !h-5 !rounded !min-w-0" />
            <Skeleton.Input active size="small" className="!w-24 !h-4 !rounded !min-w-0" />
          </div>
        </div>
      ))}
    </div>
  );
};

interface HistoryProps {
  items: HistoryItemProps[];
  /** 为 true 时展示骨架屏 */
  loading?: boolean;
  showAll?: boolean;
  defaultShowCount?: number;
}

export const History = (props: HistoryProps) => {
  const { items, loading, showAll = false, defaultShowCount = 2 } = props;
  const { t } = useTranslate();
  const [more, { toggle: toggleMore }] = useToggle(false);

  const visibleItems = useMemo(() => {
    if (showAll || more) {
      return items;
    }
    return items.slice(0, defaultShowCount);
  }, [showAll, more, items, defaultShowCount]);

  if (loading) {
    return <HistorySkeleton />;
  }

  return (
    <div className="flex flex-col">
      {visibleItems.map((item, index) => (
        <Item
          key={index}
          title={item.title}
          description={item.description}
          last={index === visibleItems.length - 1}
        />
      ))}
      {!showAll && items.length > defaultShowCount ? (
        <ToggleButton
          folded={!more}
          onToggle={toggleMore}
          foldText={t('phrase.展开')}
          unfoldText={t('phrase.收起')}
        />
      ) : null}
    </div>
  );
};
